import { useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Cross2Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { Button, Input } from '@/components/ui'
import {
  filterInternalToolsByCategory,
  getVisibleInternalTools,
  sortInternalToolsByCategory,
} from '@/lib/internalToolAccess'
import { internalToolCategories, internalTools } from '@/lib/internalToolRegistry'
import { searchInternalTools } from '@/lib/internalToolSearch'
import { ToolTile } from '@/components/layout/ToolTile'
import { getToolCategoryStyle } from '@/components/layout/toolCategoryStyles'
import { cn } from '@/lib/utils'

export function HomePage() {
  const { user } = useOutletContext()
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('All')
  const searchQuery = query.trim()
  const visibleTools = useMemo(() => getVisibleInternalTools(internalTools, user), [user])
  const categoryOptions = useMemo(
    () =>
      internalToolCategories
        .map((categoryName) => ({
          categoryName,
          count: visibleTools.filter((tool) => tool.category === categoryName).length,
        }))
        .filter(({ count }) => count),
    [visibleTools],
  )
  const displayedTools = useMemo(() => {
    if (searchQuery) return searchInternalTools(visibleTools, searchQuery)

    return sortInternalToolsByCategory(
      filterInternalToolsByCategory(visibleTools, category),
      internalToolCategories,
    )
  }, [category, searchQuery, visibleTools])

  const selectCategory = (categoryName) => {
    setCategory(categoryName)
    setQuery('')
  }

  const sectionTitle = category === 'All' ? 'All apps' : category

  return (
    <div className="min-h-full bg-background px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-5">
      <div
        className="mx-auto max-w-7xl animate-in fade-in slide-in-from-bottom-2 duration-300 motion-reduce:animate-none"
        data-cy="launch-center-content"
      >
        <div className="lg:grid lg:grid-cols-[minmax(18rem,0.8fr)_minmax(32rem,1.2fr)] lg:items-end lg:gap-10">
          <div className="text-center lg:text-left">
            <h1 className="text-balance text-3xl font-semibold text-foreground sm:text-4xl">
              Launch Center
            </h1>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base lg:mx-0">
              Find an app by name, team, or workflow.
            </p>
          </div>

          <div className="relative mt-5 lg:mt-0">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="text"
              inputMode="search"
              autoComplete="off"
              spellCheck={false}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                if (event.target.value.trim()) setCategory('All')
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setQuery('')
              }}
              placeholder="Try billing, Entrata, renewals, or resident outreach"
              aria-label="Search Launch Center"
              className="h-14 rounded-lg border-input bg-card pl-12 pr-12 text-base shadow-sm"
              data-cy="home-tool-search"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="absolute right-1.5 top-1/2 flex h-[44px] w-[44px] -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Cross2Icon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-5 md:mt-4 md:grid md:grid-cols-[12rem_minmax(0,1fr)] md:items-start md:gap-4 lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-6">
          <aside className="hidden md:block" aria-label="App categories">
            <div className="sticky top-20 rounded-lg border border-border bg-card p-2 shadow-sm">
              <p className="px-3 pb-2 pt-1 text-xs font-medium text-muted-foreground">
                Categories
              </p>
              <nav className="space-y-1" aria-label="Filter apps by category">
                <button
                  type="button"
                  onClick={() => selectCategory('All')}
                  aria-pressed={category === 'All'}
                  className={`flex min-h-10 w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    category === 'All'
                      ? 'bg-accent text-accent-foreground'
                      : 'text-foreground hover:bg-muted/60'
                  }`}
                >
                  <span>All apps</span>
                  <span className="tabular-nums text-xs text-muted-foreground">
                    {visibleTools.length}
                  </span>
                </button>
                {categoryOptions.map(({ categoryName, count }) => (
                  <button
                    key={categoryName}
                    type="button"
                    onClick={() => selectCategory(categoryName)}
                    aria-pressed={category === categoryName}
                    className={`flex min-h-10 w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm font-medium leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      category === categoryName
                        ? 'bg-accent text-accent-foreground'
                        : 'text-foreground hover:bg-muted/60'
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className={cn(
                          'h-2.5 w-2.5 shrink-0 rounded-full',
                          getToolCategoryStyle(categoryName).dot,
                        )}
                        aria-hidden="true"
                      />
                      <span>{categoryName}</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
                      {count}
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          <div className="min-w-0">
            <div className="md:hidden">
              <label className="sr-only" htmlFor="home-category-select">
                Filter apps by category
              </label>
              <select
                id="home-category-select"
                value={category}
                onChange={(event) => selectCategory(event.target.value)}
                className="h-[44px] w-full rounded-md border border-input bg-card px-3 text-sm font-medium text-foreground shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="All">All categories</option>
                {categoryOptions.map(({ categoryName, count }) => (
                  <option key={categoryName} value={categoryName}>
                    {categoryName} ({count})
                  </option>
                ))}
              </select>

            </div>

            <div className="mt-5 flex items-end justify-between gap-4 border-b border-border pb-2 md:mt-0">
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {searchQuery ? `Results for “${searchQuery}”` : sectionTitle}
                </h2>
                <p className="mt-0.5 text-sm text-muted-foreground" aria-live="polite">
                  {displayedTools.length} {displayedTools.length === 1 ? 'app' : 'apps'}
                </p>
              </div>
              {query && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setQuery('')}>
                  Clear search
                </Button>
              )}
            </div>

            {displayedTools.length ? (
              <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                {displayedTools.map((item) => (
                  <ToolTile key={item.key} item={item} />
                ))}
              </div>
            ) : (
              <div className="flex min-h-64 flex-col items-center justify-center text-center">
                <p className="font-medium text-foreground">
                  {visibleTools.length
                    ? `No apps found for “${searchQuery}”.`
                    : 'No apps are available for your account.'}
                </p>
                <p className="mt-1 max-w-md text-sm text-muted-foreground">
                  {visibleTools.length
                    ? 'Try another app name, team, system, or workflow.'
                    : 'Ask an admin to update your app access.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
